import { useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Wand2 } from "lucide-react";

/** Iter 172 · Bulk edit brands — status / client / industry / sub_industry. */

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "archived", label: "Archived" },
];

export default function BrandBulkEditDialog({ wsId, ids, clients, onClose, onSaved }) {
  const [patch, setPatch] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setPatch((p) => {
    const nx = { ...p };
    if (v === "" || v === "_nochange") delete nx[k]; else nx[k] = v;
    return nx;
  });

  const save = async () => {
    if (Object.keys(patch).length === 0) {
      toast.error("Select at least one field to update");
      return;
    }
    setBusy(true);
    try {
      const body = { ids, patch: { ...patch } };
      if (body.patch.client_id === "_none") body.patch.client_id = null;
      const r = await api.patch(`/workspaces/${wsId}/brands/bulk`, body);
      toast.success(`Updated ${r.data.modified} of ${ids.length} brand${ids.length === 1 ? "" : "s"}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk update failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="brand-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" /> Bulk edit — {ids.length} brand{ids.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Only the fields you fill are applied. Empty fields leave existing values untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Status</Label>
            <Select value={patch.status || "_nochange"} onValueChange={(v) => set("status", v === "_nochange" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-none" data-testid="brand-bulk-status-select">
                <SelectValue placeholder="— no change —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_nochange">— no change —</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Client</Label>
            <Select value={patch.client_id || "_nochange"} onValueChange={(v) => set("client_id", v === "_nochange" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-none" data-testid="brand-bulk-client-select">
                <SelectValue placeholder="— no change —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_nochange">— no change —</SelectItem>
                <SelectItem value="_none">Clear client (no client)</SelectItem>
                {(clients || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Industry</Label>
              <input value={patch.industry || ""}
                       onChange={(e) => set("industry", e.target.value)}
                       placeholder="— no change —"
                       className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                       data-testid="brand-bulk-industry-input" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Sub-industry</Label>
              <input value={patch.sub_industry || ""}
                       onChange={(e) => set("sub_industry", e.target.value)}
                       placeholder="— no change —"
                       className="h-9 w-full border border-border rounded-none px-2 text-sm bg-background"
                       data-testid="brand-bulk-sub-industry-input" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy}
                    data-testid="brand-bulk-edit-save">
            {busy ? "Applying…" : `Apply to ${ids.length} brand${ids.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
